/**
 * AppHeader component
 *
 * Top header with company selector, HireAra branding, breadcrumbs, and user menu
 */

import * as React from "react"
import { Link } from "react-router-dom"
import { ChevronDown, ChevronRight } from "lucide-react"
import { cn } from "@shadcn/lib/utils"
import type { BreadcrumbItem } from "shared/src/contexts"
import { lazyWithForcedRefresh } from "shared/src/libs/lazyLoading/lazyLoading"
import HireAraLogo from "shared/src/images/HireAraLogoPurple.png"

const AccessButton = lazyWithForcedRefresh(
	() => import(/* webpackChunkName: "AccessButton" */ "shared/src/components/AppSkeleton/AccessButton")
)

export interface AppHeaderProps {
	/**
	 * Access ID token for EVO integration
	 */
	accessIdToken?: string
	/**
	 * Client ID for EVO integration
	 */
	clientId?: string
	/**
	 * Whether EVO integration is enabled
	 */
	evoEnabled?: boolean
	/**
	 * Breadcrumb items to display after HireAra logo
	 */
	breadcrumbs?: BreadcrumbItem[]
	/**
	 * Company information
	 */
	company?: {
		name: string
		initial?: string
		color?: string
	}
	/**
	 * User information
	 */
	user?: {
		name: string
		email: string
	}
	/**
	 * Logout handler
	 */
	onLogout?: () => void
	/**
	 * Company logo URL
	 */
	logo?: string | null
	/**
	 * Additional class name
	 */
	className?: string
}

/**
 * AppHeader displays the top navigation bar with company selector, branding, breadcrumbs, and user menu.
 *
 * @example
 * ```tsx
 * <AppHeader
 *   breadcrumbs={["Candidates"]}
 *   company={{ name: "Access", initial: "A", color: "bg-red-600" }}
 *   user={{ name: "John Doe", email: "john@example.com" }}
 *   onLogout={() => console.log("logout")}
 * />
 * ```
 */
export function AppHeader({
	accessIdToken,
	clientId,
	evoEnabled,
	breadcrumbs = [],
	company,
	user,
	onLogout,
	logo,
	className,
}: Readonly<AppHeaderProps>) {
	return (
		<header
			className={cn(
				"flex h-12 md:h-16 items-center pl-12 pr-2 md:px-4 border-b sticky top-0 z-20 bg-background",
				className
			)}>
			{/* Left side: Company selector + HireAra branding + Breadcrumbs */}
			<div className="flex items-center gap-1 md:gap-2 min-w-0">
				{/* AccessButton for EVO integration */}
				{accessIdToken && evoEnabled && (
					<AccessButton accessIdToken={accessIdToken} clientId={clientId} evoEnabled={evoEnabled} />
				)}

				{/* Company Selector */}
				{/* {company && (
					<>
						<div className="flex items-center gap-2 rounded-full border px-3 py-1.5 shadow-sm hover:bg-muted cursor-pointer transition-colors">
							<div
								className={cn("flex h-6 w-6 items-center justify-center rounded-full", company.color || "bg-red-600")}>
								<span className="text-white text-[10px] font-bold">
									{company.initial || company.name.charAt(0).toUpperCase()}
								</span>
							</div>
							<span className="text-sm font-medium text-foreground">{company.name}</span>
							<ChevronDown className="h-4 w-4 text-muted-foreground" />
						</div>
						<ChevronRight className="h-5 w-5 text-muted-foreground" />
					</>
				)} */}

				{/* HireAra Branding */}
				<Link to="/candidates">
					<img src={HireAraLogo} alt="HireAra" className="h-5 md:h-7 pb-[2px] md:pb-[4px] w-auto shrink-0" />
				</Link>

				{/* Breadcrumbs - hidden on very small screens */}
				{breadcrumbs.map((crumb, index) => (
					<React.Fragment key={index}>
						<ChevronRight className="h-4 w-4 text-foreground shrink-0 hidden sm:block" />
						{crumb.href ? (
							<Link
								to={crumb.href}
								className="text-md font-medium text-foreground hover:text-foreground/80 transition-colors truncate hidden sm:block">
								{crumb.label}
							</Link>
						) : (
							<span className="text-md font-medium text-foreground truncate hidden sm:block">{crumb.label}</span>
						)}
					</React.Fragment>
				))}
			</div>

			{/* Right side: User menu */}
			<div className="ml-auto flex items-center gap-2 md:gap-4 shrink-0">
				{user && (
					<div className="relative group">
						<button className="flex items-center gap-1 md:gap-2 rounded-md px-2 md:px-3 py-1 md:py-1.5 text-xs md:text-sm hover:bg-muted transition-colors">
							<span className="text-foreground hidden sm:inline">Hello, {user.name || user.email}</span>
							<span className="text-foreground sm:hidden">{(user.name || user.email).split(" ")[0]}</span>
							<ChevronDown className="h-3 w-3 md:h-4 md:w-4 text-muted-foreground" />
						</button>

						{/* Dropdown menu */}
						{onLogout && (
							<div className="absolute right-0 top-full mt-1 w-48 rounded-md border bg-popover shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
								<div className="p-2">
									<button
										onClick={onLogout}
										className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent transition-colors">
										Sign out
									</button>
								</div>
							</div>
						)}
					</div>
				)}
				{logo ? (
					<img alt="logo" src={logo} className="h-8 md:h-[60px] max-w-[80px] md:max-w-[145px] object-contain" />
				) : (
					<div className="h-8 md:h-[60px]"></div>
				)}
			</div>
		</header>
	)
}

export default AppHeader
